import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Copy, Download, ExternalLink, HelpCircle, Loader2, ShieldAlert, Wrench, XCircle } from 'lucide-react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { cn } from '../../utils/cn';

/**
 * "How to fix" (plan Wave 4 / 4A-8): the standard fix plan rendered from the
 * reviewed playbook by GET /api/v1/recommendations/:id/fix-plan. No AI - the
 * same plan for every viewer, instant. Commands are filled only with values
 * the server validated; anything it could not fill safely shows as <LABEL>
 * and is listed under "Fill in" for the engineer.
 */

// Mirrors optimization-api src/services/fix-plans/types.ts (FixPlan).
interface RenderedStep {
  n: number;
  title: string;
  kind: 'check' | 'backup' | 'change' | 'verify';
  detail?: string;
  destructive: boolean;
  console?: string;
  cli?: string;
  terraform?: string;
  needs_input: string[];
}
interface RenderedBlocker { text: string; status: 'clear' | 'blocked' | 'check'; note?: string; check_command?: string }
interface RelatedFinding { id: string; check_id: string; title: string; status: string; potential_savings: number }
export interface FixPlan {
  recommendation_id: string;
  check_id: string;
  playbook_version: number;
  source: 'standard';
  title: string;
  cloud: string;
  problem: string;
  why_it_costs: string;
  recommended_fix: string;
  alternatives: { title: string; tradeoff: string }[];
  before_you_start: string[];
  blockers: RenderedBlocker[];
  steps: RenderedStep[];
  downtime: 'none' | 'restart' | 'maintenance_window' | 'migration' | 'app_change' | 'depends';
  downtime_note: string;
  data_risk: 'none' | 'low' | 'high';
  reversible: boolean;
  rollback: string[];
  verify: { bill_line: string; visible_after_days: number; health_check?: string };
  saving: { gross: number; added: number; net: number; note: string };
  warnings: string[];
  effort: string;
  owner_role: string;
  references: { title: string; url: string }[];
  review: { reviewed_by: string; reviewed_at: string; basis: string };
  stale_reference_data: string[];
  also_on_resource: RelatedFinding[];
  generated_at: string;
}

const DOWNTIME_LABEL: Record<FixPlan['downtime'], string> = {
  none: 'No downtime',
  restart: 'Restart (minutes)',
  maintenance_window: 'Maintenance window',
  migration: 'Migration (new resource + switch)',
  app_change: 'Application release',
  depends: 'Depends on the fix chosen',
};
const KIND_LABEL: Record<RenderedStep['kind'], string> = { check: 'Check', backup: 'Back up', change: 'Change', verify: 'Verify' };
const money = (n: number) => `$${(Math.round(n * 100) / 100).toFixed(2)}`;

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{children}</div>
);

const Chip: React.FC<{ tone?: 'neutral' | 'good' | 'warn' | 'bad'; children: React.ReactNode }> = ({ tone = 'neutral', children }) => (
  <span className={cn(
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
    tone === 'neutral' && 'bg-secondary text-foreground',
    tone === 'good' && 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    tone === 'warn' && 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    tone === 'bad' && 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
  )}>{children}</span>
);

const CodeBlock: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
        className="absolute right-2 top-2 inline-flex items-center gap-1 rounded bg-gray-800 px-1.5 py-0.5 text-[11px] text-gray-300 hover:text-white"
      >
        <Copy className="h-3 w-3" /> {copied ? 'Copied' : 'Copy'}
      </button>
      <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-900 p-3 pr-16 text-xs text-gray-100">{text}</pre>
    </div>
  );
};

const StepCard: React.FC<{ step: RenderedStep }> = ({ step }) => {
  const formats = (['cli', 'console', 'terraform'] as const).filter((f) => step[f]);
  const [format, setFormat] = useState<(typeof formats)[number] | undefined>(formats[0]);
  return (
    <div className={cn('rounded-md border p-3', step.destructive ? 'border-rose-500/40 bg-rose-500/5' : 'border-border')}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{step.n}</span>
        <span className="text-sm font-medium text-foreground">{step.title}</span>
        <Chip tone={step.kind === 'backup' ? 'good' : step.kind === 'change' ? 'warn' : 'neutral'}>{KIND_LABEL[step.kind]}</Chip>
        {step.destructive && <Chip tone="bad"><ShieldAlert className="h-3 w-3" /> Permanent — deletes / removes</Chip>}
      </div>
      {step.detail && <p className="mt-2 text-sm text-foreground/80">{step.detail}</p>}
      {step.needs_input.length > 0 && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
          Fill in before running: {step.needs_input.map((x) => <code key={x} className="mx-0.5 rounded bg-amber-500/10 px-1">&lt;{x}&gt;</code>)}
        </p>
      )}
      {formats.length > 0 && format && (
        <div className="mt-2">
          {formats.length > 1 && (
            <div className="mb-1.5 inline-flex rounded-md border border-border p-0.5">
              {formats.map((f) => (
                <button key={f} type="button" onClick={() => setFormat(f)}
                  className={cn('rounded px-2 py-0.5 text-xs', format === f ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')}>
                  {f === 'cli' ? 'CLI' : f === 'console' ? 'Console' : 'Terraform'}
                </button>
              ))}
            </div>
          )}
          {format === 'console' ? <p className="rounded bg-secondary/60 p-2 text-sm text-foreground">{step.console}</p> : <CodeBlock text={step[format] || ''} />}
        </div>
      )}
    </div>
  );
};

interface HowToFixTabProps {
  recId: string;
  /** Fetch only once the tab is first opened. */
  active: boolean;
  fetchPlan: (id: string) => Promise<{ plan: FixPlan | null; message?: string }>;
  /** The plan as Markdown from the server - the same text the ticket carries. */
  fetchMarkdown: (id: string) => Promise<string>;
  /** Accuracy rule A12: "This plan is wrong". */
  sendFeedback: (id: string, reason: string, note: string) => Promise<void>;
}

const FEEDBACK_REASONS: { value: string; label: string }[] = [
  { value: 'wrong_command', label: 'A command or step is wrong' },
  { value: 'missing_blocker', label: 'Something to check first is missing' },
  { value: 'not_applicable', label: 'This fix does not apply here' },
  { value: 'saving_wrong', label: 'The saving is wrong' },
  { value: 'unsafe', label: 'It is unsafe' },
  { value: 'other', label: 'Other' },
];

const PlanFeedback: React.FC<{ recId: string; send: HowToFixTabProps['sendFeedback'] }> = ({ recId, send }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState(FEEDBACK_REASONS[0].value);
  const [note, setNote] = useState('');
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | string>('idle');
  useEffect(() => { setOpen(false); setNote(''); setState('idle'); }, [recId]);

  if (state === 'sent') return <p className="text-xs text-emerald-700 dark:text-emerald-400">Thanks — reported to the playbook reviewers.</p>;
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="text-xs text-muted-foreground underline hover:text-foreground">This plan is wrong</button>;
  return (
    <div className="mt-2 space-y-2 rounded-md border border-border p-3">
      <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded border border-input bg-background px-2 py-1 text-sm">
        {FEEDBACK_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
      </select>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} rows={3} placeholder="What is wrong? (optional)"
        className="w-full rounded border border-input bg-background px-2 py-1 text-sm" />
      <div className="flex items-center gap-2">
        <Button size="sm" variant="primary" disabled={state === 'sending'}
          onClick={async () => {
            setState('sending');
            try { await send(recId, reason, note); setState('sent'); } catch (err: any) { setState(`Could not send: ${err?.response?.data?.message || err?.message || err}`); }
          }}>Send</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
        {state !== 'idle' && state !== 'sending' && <span className="text-xs text-rose-600">{state}</span>}
      </div>
    </div>
  );
};

export const HowToFixTab: React.FC<HowToFixTabProps> = ({ recId, active, fetchPlan, fetchMarkdown, sendFeedback }) => {
  // undefined = not fetched yet; null = fetched, no plan.
  const [plan, setPlan] = useState<FixPlan | null | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Record<number, boolean>>({});
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // "Also on this resource" links navigate to another finding while this
  // component stays mounted - start again for the new id.
  useEffect(() => {
    setPlan(undefined);
    setMessage(undefined);
    setError(null);
    setTicked({});
  }, [recId]);

  useEffect(() => {
    if (!active || plan !== undefined || loading) return;
    setLoading(true);
    fetchPlan(recId)
      .then((r) => { setPlan(r.plan); setMessage(r.message); setError(null); })
      .catch((err) => {
        setPlan(null);
        const status = err?.response?.status;
        setError(status ? `${status} ${err.response?.data?.message || err.response?.data?.error || err.message}` : err.message);
      })
      .finally(() => setLoading(false));
  }, [active, plan, loading, recId, fetchPlan]);

  if (loading || plan === undefined) {
    return <Card className="p-4"><span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading the fix plan…</span></Card>;
  }
  if (!plan) {
    return <Card className="p-4"><p className="text-sm text-muted-foreground">{error ? `Could not load the fix plan: ${error}` : message || 'No standard fix plan for this finding yet.'}</p></Card>;
  }

  const download = async () => {
    try {
      const md = await fetchMarkdown(recId);
      const url = URL.createObjectURL(new Blob([md], { type: 'text/markdown' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `fix-plan-${plan.check_id}-${recId.slice(0, 8)}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setDownloadError(`Download failed: ${err?.response?.status ? `${err.response.status} ` : ''}${err?.message || err}`);
    }
  };
  const blocked = plan.blockers.some((b) => b.status === 'blocked');

  return (
    <div className="space-y-3">
      <Card className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold text-foreground"><Wrench className="h-4 w-4 text-primary" /> {plan.title}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Chip tone={plan.downtime === 'none' ? 'good' : 'warn'}>{DOWNTIME_LABEL[plan.downtime]}</Chip>
              <Chip tone={plan.data_risk === 'high' ? 'bad' : plan.data_risk === 'low' ? 'warn' : 'good'}>Data risk: {plan.data_risk}</Chip>
              <Chip tone={plan.reversible ? 'good' : 'bad'}>{plan.reversible ? 'Can be undone' : 'Cannot be undone'}</Chip>
              <Chip>Effort: {plan.effort}</Chip>
              <Chip>Owner: {plan.owner_role}</Chip>
            </div>
          </div>
          <div className="text-right">
            <Button size="sm" variant="outline" onClick={download}><Download /> Download (.md)</Button>
            {downloadError && <div className="mt-1 text-xs text-rose-600">{downloadError}</div>}
          </div>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">{plan.downtime_note}</p>
        <p className="mt-1 text-[11px] text-muted-foreground/80">Standard plan v{plan.playbook_version} · {plan.review.reviewed_by} · {plan.review.reviewed_at}</p>
        <div className="mt-2"><PlanFeedback recId={recId} send={sendFeedback} /></div>
      </Card>

      {(plan.warnings.length > 0 || plan.stale_reference_data.length > 0) && (
        <Card className="border-amber-500/40 bg-amber-500/5 p-4">
          <Label>Before anything else</Label>
          <ul className="space-y-1">
            {[...plan.warnings, ...plan.stale_reference_data].map((w, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {w}</li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-3 md:grid-cols-3">
        <Card className="p-4 md:col-span-2">
          <Label>Problem</Label>
          <p className="text-sm text-foreground">{plan.problem}</p>
          <p className="mt-2 text-sm text-muted-foreground">{plan.why_it_costs}</p>
        </Card>
        <Card className="p-4">
          <Label>Saving (monthly)</Label>
          <div className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{money(plan.saving.net)}</div>
          {plan.saving.added > 0 && (
            <p className="mt-1 text-xs text-muted-foreground">{money(plan.saving.gross)} gross − {money(plan.saving.added)} new cost of the fix = {money(plan.saving.net)} net</p>
          )}
          {plan.saving.note && <p className="mt-1 text-xs text-muted-foreground">{plan.saving.note}</p>}
        </Card>
      </div>

      <Card className="p-4">
        <Label>Recommended fix</Label>
        <p className="text-sm text-foreground">{plan.recommended_fix}</p>
        {plan.alternatives.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-medium text-muted-foreground">Alternatives</div>
            <ul className="mt-1 space-y-1">
              {plan.alternatives.map((a) => <li key={a.title} className="text-sm"><span className="font-medium text-foreground">{a.title}</span><span className="text-muted-foreground"> — {a.tradeoff}</span></li>)}
            </ul>
          </div>
        )}
      </Card>

      <Card className={cn('p-4', blocked && 'border-rose-500/40')}>
        <Label>Check before you start</Label>
        <ul className="space-y-2">
          {plan.blockers.map((b, i) => (
            <li key={i} className="text-sm">
              <div className="flex gap-2">
                {b.status === 'clear' ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : b.status === 'blocked' ? <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" /> : <HelpCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                <div className="min-w-0 flex-1">
                  <span className={cn('text-foreground', b.status === 'blocked' && 'font-medium')}>{b.text}</span>
                  {b.note && <div className={cn('mt-0.5 text-xs', b.status === 'blocked' ? 'text-rose-700 dark:text-rose-400' : 'text-muted-foreground')}>{b.note}</div>}
                  {b.check_command && <div className="mt-1"><CodeBlock text={b.check_command} /></div>}
                </div>
              </div>
            </li>
          ))}
          {plan.before_you_start.map((t, i) => (
            <li key={`b${i}`} className="flex gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={!!ticked[i]} onChange={(e) => setTicked({ ...ticked, [i]: e.target.checked })} />
              <span className={cn('text-foreground', ticked[i] && 'text-muted-foreground line-through')}>{t}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-4">
        <Label>Steps</Label>
        {plan.steps.length === 0 ? <p className="text-sm text-muted-foreground">No steps for this cloud — needs an engineer.</p> : (
          <div className="space-y-2">{plan.steps.map((s) => <StepCard key={s.n} step={s} />)}</div>
        )}
      </Card>

      <div className="grid gap-3 md:grid-cols-2">
        <Card className="p-4">
          <Label>If it goes wrong (rollback)</Label>
          <ul className="space-y-1">{plan.rollback.map((r, i) => <li key={i} className="text-sm text-foreground">{r}</li>)}</ul>
        </Card>
        <Card className="p-4">
          <Label>How FinOps confirms the saving</Label>
          <p className="text-sm text-foreground">{plan.verify.bill_line}</p>
          <p className="mt-1 text-xs text-muted-foreground">Checked on the bill about {plan.verify.visible_after_days} days after the fix.{plan.verify.health_check ? ` Health: ${plan.verify.health_check}` : ''}</p>
        </Card>
      </div>

      {plan.also_on_resource.length > 0 && (
        <Card className="p-4">
          <Label>Also on this resource</Label>
          <ul className="space-y-1">
            {plan.also_on_resource.map((r) => (
              <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
                <Link to={`/recommendations/${r.id}`} className="font-medium text-primary hover:underline">{r.title}</Link>
                <Chip>{r.status.replace(/_/g, ' ')}</Chip>
                {r.potential_savings > 0 && <span className="text-xs text-emerald-600 dark:text-emerald-400">{money(r.potential_savings)}/mo</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {plan.references.length > 0 && (
        <Card className="p-4">
          <Label>References</Label>
          <ul className="space-y-1">
            {plan.references.map((r) => (
              <li key={r.url}><a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary hover:underline">{r.title} <ExternalLink className="h-3 w-3" /></a></li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
};

/** Default fetchers: optimization-api, with the caller's own auth headers. */
export function makeFixPlanFetcher(baseUrl: string, headers: () => Record<string, string>) {
  return async (id: string) => {
    const res = await axios.get(`${baseUrl}/api/v1/recommendations/${id}/fix-plan`, { headers: headers() });
    return { plan: (res.data?.data ?? null) as FixPlan | null, message: res.data?.message as string | undefined };
  };
}

export function makeFixPlanFeedbackSender(baseUrl: string, headers: () => Record<string, string>) {
  return async (id: string, reason: string, note: string): Promise<void> => {
    await axios.post(`${baseUrl}/api/v1/recommendations/${id}/fix-plan/feedback`, { reason, note }, { headers: headers() });
  };
}

export function makeFixPlanMarkdownFetcher(baseUrl: string, headers: () => Record<string, string>) {
  return async (id: string): Promise<string> => {
    const res = await axios.get(`${baseUrl}/api/v1/recommendations/${id}/fix-plan`, { params: { format: 'markdown' }, headers: headers(), responseType: 'text' });
    return String(res.data ?? '');
  };
}
